import { useState, useRef, useCallback, useMemo, useEffect, CSSProperties } from 'react';
import {
    NJButton,
    NJHeading,
    NJIconButton,
    NJTabs,
    NJTab,
    NJTabPanel,
    NJTooltip,
    NJToastService,
    NJToastContainer,
} from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import DataTable from '@shared/DataTable';
import Loading from '@shared/Loading';
import type {
    ColDef,
    CellValueChangedEvent as AGCellValueChangedEvent,
    GridReadyEvent,
    GridApi,
} from 'ag-grid-community';
import { useMassEditorData, useApplyMassEditorChanges } from './hooks';
import type { TabType, TabData, UndoEntry, DepartureEntity, PipeEntity, SubStationEntity, TabEntities } from './types';
import { TAB_LABELS } from './types';
import type { PrototypeMeta } from '@shared/discoverPrototypes';

export const meta: PrototypeMeta = {
    title: 'Editeur en masse - Nouvelle toolbar',
    description: 'Refonte de la toolbar : compteur de lignes, raccourcis clavier, import/export, copie, undo, plein ecran.',
    status: 'draft',
    tags: ['network-editor', 'mass-editor', 'toolbar', 'inline-edit'],
    author: 'erika.dalmonte',
};

const TABS: TabType[] = ['Departure', 'Pipe', 'Return', 'SubStation'];

// Champs en lecture seule — identique a READ_ONLY_FIELDS de MassEditorTable.ts
const RO_FIELDS = new Set(['identifier', 'latitude', 'longitude', 'projectedLength']);

// Styles CSS inline (Tailwind absent du studio — on ne peut pas utiliser getCellClassName)
const RO_CELL_STYLE = {
    background: 'var(--nj-semantic-color-background-neutral-tertiary-hovered)',
    color: 'var(--nj-semantic-color-text-secondary-default)',
};
const SEL_CELL_STYLE = { background: '#e8f0fe' };

// Formateur : affiche '-' pour les valeurs nulles/vides
const DASH_FORMATTER = ({ value }: { value: unknown }) =>
    value === null || value === undefined || value === '' ? '-' : String(value);

// Construit une ColDef avec cellStyle pour le grisage et context.readOnly
function mkCol<T>(h: string, f: keyof T): ColDef<T> {
    const readOnly = RO_FIELDS.has(f as string);
    return {
        headerName: h,
        field: f as unknown as ColDef<T>['field'],
        editable: !readOnly,
        sortable: false,
        resizable: true,
        filter: true,
        minWidth: 120,
        valueFormatter: DASH_FORMATTER,
        cellStyle: readOnly ? RO_CELL_STYLE : undefined,
        // context.readOnly stocke l'etat pour la selection de colonne
        ...({ context: { readOnly } } as Record<string, unknown>),
    } as ColDef<T>;
}

const DEP_COLDEFS: ColDef<DepartureEntity>[] = [
    mkCol('Identifiant', 'identifier'),
    mkCol('Nom', 'name'),
    mkCol('Debit massique maxi [kg/s]', 'maxOutputFlow'),
    mkCol('T depart a Text min [C]', 'profileTMin'),
    mkCol('T depart a Text inter [C]', 'profileTInter'),
    mkCol('T depart a Text max [C]', 'profileTMax'),
    mkCol('* Altitude [m]', 'altitude'),
    mkCol('Latitude', 'latitude'),
    mkCol('Longitude', 'longitude'),
];

const PIPE_COLDEFS: ColDef<PipeEntity>[] = [
    mkCol('Identifiant', 'identifier'),
    mkCol('Nom', 'name'),
    mkCol('* Direction', 'pipeUsage'),
    mkCol('* Longueur reelle [m]', 'realLength'),
    mkCol('Longueur projetee [m]', 'projectedLength'),
    mkCol('* Diametre interieur [mm]', 'innerDiameter'),
    mkCol('* Diametre nominal [mm]', 'nominalDiameter'),
    mkCol('* Facteur de rugosite', 'roughnessFactor'),
    mkCol('Coeff. echange [W/m2K]', 'heatExchangeCoef'),
    mkCol('Redimensionnable', 'resizable'),
];

interface ReturnRow { id: string; identifier: string; name: string; altitude: number; latitude: number; longitude: number; }
const RET_COLDEFS: ColDef<ReturnRow>[] = [
    mkCol('Identifiant', 'identifier'),
    mkCol('Nom', 'name'),
    mkCol('Altitude [m]', 'altitude'),
    mkCol('Latitude', 'latitude'),
    mkCol('Longitude', 'longitude'),
];

const SST_COLDEFS: ColDef<SubStationEntity>[] = [
    mkCol('Identifiant', 'identifier'),
    mkCol('Nom', 'name'),
    mkCol('* Active', 'status'),
    mkCol('Puissance nominale [kW]', 'nominalPower'),
    mkCol('DeltaP nominal [bar]', 'nominalPressureLoss'),
    mkCol('DeltaT nominal [C]', 'nominalDeltaT'),
    mkCol('Delta P vanne min [bar]', 'valveMinPressureLoss'),
    mkCol('Puissance a Text min [kW]', 'consumptionMin'),
    mkCol('Puissance a Text inter [kW]', 'consumptionInter'),
    mkCol('Puissance a Text max [kW]', 'consumptionMax'),
    mkCol('Delta T min [C]', 'deltaTMin'),
    mkCol('Delta T inter [C]', 'deltaTInter'),
    mkCol('Delta T max [C]', 'deltaTMax'),
    mkCol('T sortie min [C]', 'tOutMin'),
    mkCol('T sortie inter [C]', 'tOutInter'),
    mkCol('T sortie max [C]', 'tOutMax'),
    mkCol('T alim max [C]', 'tSupplyMax'),
    mkCol('Altitude [m]', 'altitude'),
    mkCol('Latitude', 'latitude'),
    mkCol('Longitude', 'longitude'),
];

const COLDEFS_BY_TAB: Record<TabType, ColDef<object>[]> = {
    Departure: DEP_COLDEFS as ColDef<object>[],
    Pipe: PIPE_COLDEFS as ColDef<object>[],
    Return: RET_COLDEFS as ColDef<object>[],
    SubStation: SST_COLDEFS as ColDef<object>[],
};

const DEFAULT_COL_DEF: ColDef = { sortable: false, resizable: true, minWidth: 100, filter: true };

const SHORTCUTS = [
    { keys: 'Tab', desc: 'Passer a la cellule suivante' },
    { keys: 'Shift + Tab', desc: 'Passer a la cellule precedente' },
    { keys: 'Entree', desc: "Valider et descendre d'une ligne" },
    { keys: 'Echap', desc: "Annuler l'edition en cours" },
    { keys: 'F2', desc: 'Entrer en mode edition' },
    { keys: 'Ctrl + Z', desc: 'Annuler la derniere modification' },
    { keys: 'Ctrl + C', desc: 'Copier la cellule ou colonne selectionnee' },
    { keys: 'Ctrl + V', desc: 'Coller dans la colonne selectionnee (compatible Excel)' },
    { keys: 'Clic sur en-tete de colonne', desc: 'Selectionner toute la colonne pour copy/paste' },
    { keys: 'Suppr / Retour', desc: 'Effacer la valeur de la cellule' },
    { keys: 'Fleches', desc: 'Naviguer entre les cellules' },
];

// ─── Sous-composant : grille avec selection de colonne par clic en-tete ─────
interface GridPanelProps {
    testId: string;
    colDefs: ColDef<object>[];
    data: object[];
    onCellValueChanged: (e: AGCellValueChangedEvent) => void;
    onGridReady?: (e: GridReadyEvent) => void;
    onToast?: (msg: string, icon: string, variant: 'success' | 'info') => void;
    onUndoPush?: (entry: UndoEntry) => void;
    tabType: TabType;
}

function MassEditorGridPanel({ testId, colDefs, data, onCellValueChanged, onGridReady, onToast, onUndoPush, tabType }: GridPanelProps) {
    const gridWrapRef = useRef<HTMLDivElement>(null);
    const gridApiRef = useRef<GridApi | null>(null);
    // Ref pour que la fonction cellStyle puisse lire la sélection courante sans recréer les ColDefs
    const selectedRef = useRef<number>(-1); // index de la colonne sélectionnée, -1 = aucune

    // Columns with cellStyle as a function — AG Grid re-évalue à chaque refreshCells
    const headers = useMemo(
        () =>
            colDefs.map((colDef, i) => {
                const isRO = !!((colDef as unknown as { context?: { readOnly?: boolean } }).context?.readOnly);
                return {
                    ...colDef,
                    cellStyle: () => {
                        if (isRO) return RO_CELL_STYLE;
                        if (selectedRef.current === i) return SEL_CELL_STYLE;
                        return undefined;
                    },
                };
            }),
        [colDefs]
    );

    // Reset selection quand les colonnes changent (changement d'onglet)
    useEffect(() => {
        selectedRef.current = -1;
        gridApiRef.current?.redrawRows();
    }, [colDefs]);

    // Capture l'API AG Grid dès que la grille est prête
    const handleGridReady = useCallback((e: GridReadyEvent) => {
        gridApiRef.current = e.api;
        onGridReady?.(e);
    }, [onGridReady]);

    // Clic sur en-tête de colonne = sélectionner / désélectionner (une seule à la fois)
    useEffect(() => {
        const gridEl = gridWrapRef.current;
        if (!gridEl) return;
        const onClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            // Cherche la cellule d'en-tête la plus proche (quelle que soit la cible exacte)
            const headerCell = target.closest('.ag-header-cell');
            if (headerCell) {
                const colId = headerCell.getAttribute('col-id');
                if (colId) {
                    const idx = colDefs.findIndex((c) => c.field === colId);
                    if (idx >= 0) {
                        // Toggle : re-clic sur la même colonne = désélection
                        selectedRef.current = selectedRef.current === idx ? -1 : idx;
                        gridApiRef.current?.redrawRows();
                    }
                }
            } else {
                // Clic sur cellule = désélectionner
                if (target.closest('.ag-cell') && selectedRef.current !== -1) {
                    selectedRef.current = -1;
                    gridApiRef.current?.redrawRows();
                }
            }
        };
        gridEl.addEventListener('click', onClick);
        return () => gridEl.removeEventListener('click', onClick);
    }, [colDefs]);

    // ── Ctrl+C : copier la valeur de la cellule focalisée dans le presse-papiers
    // ── Ctrl+V : coller la valeur dans toutes les cellules de la colonne sélectionnée
    useEffect(() => {
        const gridEl = gridWrapRef.current;
        if (!gridEl) return;

        const onKeyDown = async (event: KeyboardEvent) => {
            const isMac = navigator.platform.toUpperCase().includes('MAC');
            const ctrlOrMeta = isMac ? event.metaKey : event.ctrlKey;
            if (!ctrlOrMeta) return;

            const api = gridApiRef.current;
            if (!api) return;

            // ── Ctrl+C : copier la cellule focalisée (seulement si pas de colonne sélectionnée)
            if ((event.key === 'c' || event.key === 'C') && selectedRef.current === -1) {
                const focused = api.getFocusedCell();
                if (!focused) return;
                const editingCells = api.getEditingCells();
                if (editingCells.length > 0) return;
                const rowNode = api.getDisplayedRowAtIndex(focused.rowIndex);
                if (!rowNode?.data) return;
                const colId = focused.column.getColId();
                const raw = (rowNode.data as Record<string, unknown>)[colId];
                const text = raw == null ? '' : String(raw);
                try {
                    await navigator.clipboard.writeText(text);
                    onToast?.(`Valeur copiée : ${text || '(vide)'}`, 'content_copy', 'success');
                    event.preventDefault();
                    event.stopPropagation();
                } catch { /* clipboard access denied */ }
                return;
            }

            // ── Ctrl+V : coller dans la colonne sélectionnée
            if ((event.key === 'v' || event.key === 'V') && selectedRef.current !== -1) {
                const selIdx = selectedRef.current;
                const colDef = colDefs[selIdx];
                if (!colDef?.field) return;
                const isRO = !!((colDef as unknown as { context?: { readOnly?: boolean } }).context?.readOnly);
                if (isRO) {
                    onToast?.('Cette colonne est en lecture seule', 'lock', 'info');
                    event.preventDefault();
                    return;
                }
                let text: string;
                try {
                    text = await navigator.clipboard.readText();
                } catch {
                    onToast?.('Accès au presse-papiers refusé', 'error', 'info');
                    return;
                }
                const fieldName = colDef.field as string;
                const value: string | number = isNaN(Number(text)) || text.trim() === '' ? text : Number(text);
                let count = 0;
                api.forEachNodeAfterFilterAndSort((rowNode) => {
                    if (!rowNode.data) return;
                    const oldValue = (rowNode.data as Record<string, unknown>)[fieldName];
                    onUndoPush?.({ tabType, rowId: rowNode.data['id'] as string, field: fieldName, oldValue });
                    rowNode.setDataValue(fieldName, value);
                    count++;
                });
                onToast?.(`${count} cellules mises à jour`, 'check_circle', 'success');
                // Désélectionner après le paste
                selectedRef.current = -1;
                api.redrawRows();
                event.preventDefault();
                event.stopPropagation();
            }
        };

        gridEl.addEventListener('keydown', onKeyDown);
        return () => gridEl.removeEventListener('keydown', onKeyDown);
    }, [colDefs, onToast, onUndoPush, tabType]);

    return (
        <div ref={gridWrapRef} style={{ position: 'relative', margin: '12px 0 0 0' }}>
            <DataTable
                testId={testId}
                header={headers}
                data={data}
                defaultColDef={DEFAULT_COL_DEF}
                verticalScroll
                onCellValueChanged={onCellValueChanged}
                onGridReady={handleGridReady}
            />
        </div>
    );
}

// ─── Modale raccourcis clavier ────────────────────────────────────────────────
function ShortcutsModal({ onClose }: { onClose: () => void }) {
    useEffect(() => {
        const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [onClose]);
    return (
        <div style={sty.overlay} onClick={onClose}>
            <div style={sty.scPanel} onClick={(e) => e.stopPropagation()}>
                <div style={sty.scHeader}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>Raccourcis clavier</span>
                    <NJIconButton icon="close" label="Fermer" scale="sm" variant="tertiary" onClick={onClose} />
                </div>
                <div style={sty.scList}>
                    {SHORTCUTS.map(({ keys, desc }) => (
                        <div key={keys} style={sty.scRow}>
                            <code style={sty.scKey}>{keys}</code>
                            <span style={{ fontSize: 13, color: 'var(--nj-semantic-color-text-secondary-default)' }}>{desc}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ─── Toolbar (icons + chip N lignes) ─────────────────────────────────────────
function Toolbar({ rowCount, canUndo, isFullscreen, onShortcuts, onDownload, onUpload, onCopyAll, onUndo, onToggleFullscreen }: {
    rowCount: number; canUndo: boolean; isFullscreen: boolean;
    onShortcuts: () => void; onDownload: () => void; onUpload: () => void;
    onCopyAll: () => void; onUndo: () => void; onToggleFullscreen: () => void;
}) {
    return (
        <div style={sty.toolbar}>
            <div style={sty.lineChip}>
                <span style={sty.lineText}>{rowCount} lignes</span>
            </div>
            <NJTooltip text="Raccourcis clavier" hasArrow placement="bottom">
                <NJIconButton icon="keyboard" label="Raccourcis clavier" scale="xs" variant="tertiary" onClick={onShortcuts} />
            </NJTooltip>
            <span style={sty.divider} />
            <NJTooltip text="Exporter vers Excel" hasArrow placement="bottom">
                <NJIconButton icon="download" label="Exporter" scale="xs" variant="tertiary" onClick={onDownload} />
            </NJTooltip>
            <NJTooltip text="Importer depuis Excel" hasArrow placement="bottom">
                <NJIconButton icon="upload" label="Importer" scale="xs" variant="tertiary" onClick={onUpload} />
            </NJTooltip>
            <NJTooltip text="Copier tout le tableau (TSV)" hasArrow placement="bottom">
                <NJIconButton icon="content_copy" label="Copier" scale="xs" variant="tertiary" onClick={onCopyAll} />
            </NJTooltip>
            <NJTooltip text="Annuler (Ctrl+Z)" hasArrow placement="bottom">
                <NJIconButton icon="replay" label="Annuler" scale="xs" variant="tertiary" isDisabled={!canUndo} onClick={onUndo} />
            </NJTooltip>
            <NJTooltip text={isFullscreen ? 'Quitter le plein ecran' : 'Plein ecran'} hasArrow placement="bottom">
                <NJIconButton icon={isFullscreen ? 'fullscreen_exit' : 'fullscreen'} label="Plein ecran" scale="xs" variant="tertiary" onClick={onToggleFullscreen} />
            </NJTooltip>
        </div>
    );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function MassEditorToolbarPrototype() {
    const { data: remoteData, isLoading, isError, refetch } = useMassEditorData();
    const applyMutation = useApplyMassEditorChanges();
    const [tableData, setTableData] = useState<TabData | null>(null);
    useEffect(() => {
        if (remoteData && !tableData) setTableData({
            Departure: remoteData.Departure.map((r) => ({ ...r })),
            Pipe: remoteData.Pipe.map((r) => ({ ...r })),
            Return: remoteData.Return.map((r) => ({ ...r })),
            SubStation: remoteData.SubStation.map((r) => ({ ...r })),
        });
    }, [remoteData, tableData]);

    const [activeTab, setActiveTab] = useState<TabType>('Departure');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const undoStack = useRef<UndoEntry[]>([]);
    const gridApiRef = useRef<GridApi | null>(null);

    const showToast = useCallback((msg: string, iconName: string, _variant: 'success' | 'info' = 'success') => {
        NJToastService(msg, {
            id: `me-toast-${Date.now()}`,
            iconName,
            shouldDismiss: true,
            dismissAfter: 2500,
            hasCloseIcon: true,
            isInverse: false,
        });
    }, []);
    const rowCount = useMemo(() => tableData ? tableData[activeTab].length : 0, [tableData, activeTab]);
    const [canUndo, setCanUndo] = useState(false);

    const handleGridReady = useCallback((e: GridReadyEvent) => { gridApiRef.current = e.api; }, []);

    const onCellChanged = useCallback((e: AGCellValueChangedEvent, tab: TabType) => {
        undoStack.current.push({ tabType: tab, rowId: (e.data as { id: string }).id, field: e.colDef.field as string, oldValue: e.oldValue });
        setCanUndo(true);
        setTableData((p) => p ? { ...p } : p);
    }, []);

    const handleUndo = useCallback(() => {
        const entry = undoStack.current.pop();
        if (!entry || !tableData) return;
        setTableData((p) => {
            if (!p) return p;
            return { ...p, [entry.tabType]: p[entry.tabType].map((r: TabEntities) => r.id === entry.rowId ? { ...r, [entry.field]: entry.oldValue } : r) };
        });
        setCanUndo(undoStack.current.length > 0);
        showToast('Modification annulee', 'replay');
    }, [tableData, showToast]);

    // Ctrl+Z / Cmd+Z global → undo
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            const isMac = navigator.platform.toUpperCase().includes('MAC');
            const ctrl = isMac ? e.metaKey : e.ctrlKey;
            if (ctrl && (e.key === 'z' || e.key === 'Z') && !e.shiftKey) {
                e.preventDefault();
                handleUndo();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [handleUndo]);

    const handleCopyAll = useCallback(() => {
        if (!tableData) return;
        const rows = tableData[activeTab] as unknown as Record<string, unknown>[];
        if (!rows.length) return;
        const ks = Object.keys(rows[0]).filter((k) => k !== 'id');
        const tsv = [ks.join('\t'), ...rows.map((r) => ks.map((k) => String(r[k] ?? '')).join('\t'))].join('\n');
        navigator.clipboard.writeText(tsv)
            .then(() => showToast('Tableau copie dans le presse-papiers', 'content_copy', 'info'))
            .catch(() => showToast('Impossible de copier', 'error', 'info'));
    }, [tableData, activeTab, showToast]);

    const handleDownload = useCallback(() => {
        if (!tableData) return;
        const rows = tableData[activeTab] as unknown as Record<string, unknown>[];
        if (!rows.length) return;
        const ks = Object.keys(rows[0]).filter((k) => k !== 'id');
        const csv = [ks.join(';'), ...rows.map((r) => ks.map((k) => String(r[k] ?? '')).join(';'))].join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url; a.download = `mass-editor-${activeTab.toLowerCase()}.csv`; a.click();
        URL.revokeObjectURL(url);
        showToast('Export telecharge', 'download');
    }, [tableData, activeTab, showToast]);

    const handleUpload = useCallback(() => showToast('Import Excel - a implementer', 'upload', 'info'), [showToast]);

    const handleApply = useCallback(() => {
        if (!tableData) return;
        applyMutation.mutate(tableData, {
            onSuccess: () => { undoStack.current = []; setCanUndo(false); showToast('Modifications validees', 'check_circle'); },
            onError: () => showToast('Erreur lors de la sauvegarde', 'error', 'info'),
        });
    }, [tableData, applyMutation, showToast]);

    const handleCancel = useCallback(() => {
        if (remoteData) {
            setTableData({
                Departure: remoteData.Departure.map((r) => ({ ...r })),
                Pipe: remoteData.Pipe.map((r) => ({ ...r })),
                Return: remoteData.Return.map((r) => ({ ...r })),
                SubStation: remoteData.SubStation.map((r) => ({ ...r })),
            });
            undoStack.current = [];
            setCanUndo(false);
        }
    }, [remoteData]);

    if (isLoading) return <PrototypeStudioLayout title={meta.title}><Loading text="Chargement..." scale="lg" /></PrototypeStudioLayout>;
    if (isError || !tableData) return (
        <PrototypeStudioLayout title={meta.title}>
            <div style={sty.errState}>
                <span className="material-icons-outlined" style={{ fontSize: 40, color: 'var(--nj-semantic-color-text-error-default)' }}>error_outline</span>
                <span>Impossible de charger les donnees</span>
                <NJButton label="Reessayer" variant="secondary" onClick={() => void refetch()} />
            </div>
        </PrototypeStudioLayout>
    );

    const modalSty: CSSProperties = isFullscreen ? sty.modalFs : sty.modal;

    return (
        <PrototypeStudioLayout title={meta.title}>
            <div style={sty.page}>
                {/* Fond carte reseau */}
                <div style={sty.mapBg} aria-hidden="true">
                    <svg style={{ width: '100%', height: '100%' }} viewBox="0 0 1200 600" preserveAspectRatio="xMidYMid slice">
                        <path d="M 80,300 Q 200,200 350,260 T 600,220 L 850,180 Q 950,160 1050,210 L 1150,260" stroke="var(--nj-semantic-color-border-error-default)" strokeWidth="3" fill="none" opacity="0.4" />
                        <path d="M 90,312 Q 210,212 360,272 T 610,232 L 860,192 Q 960,172 1060,222 L 1160,272" stroke="var(--nj-semantic-color-border-brand-default)" strokeWidth="3" fill="none" opacity="0.4" />
                        <path d="M 350,260 L 380,430 Q 400,530 460,550" stroke="var(--nj-semantic-color-border-error-default)" strokeWidth="2" fill="none" opacity="0.3" />
                        <path d="M 600,220 L 630,390 L 680,510" stroke="var(--nj-semantic-color-border-error-default)" strokeWidth="2" fill="none" opacity="0.3" />
                        {[80,350,600,850,460,680].map((cx,i) => <circle key={i} cx={cx} cy={[300,260,220,180,550,510][i]} r={4} fill="var(--nj-semantic-color-text-brand-default)" opacity="0.6" />)}
                    </svg>
                    <span style={sty.mapLbl}><span className="material-icons-outlined" style={{ fontSize: 13, marginRight: 4 }}>map</span>Vue reseau</span>
                </div>

                {/* Modal */}
                <div style={modalSty}>

                    {/* ── Ligne 1 : bouton Fermer seul, aligne a droite ── */}
                    <div style={sty.closeRow}>
                        <NJIconButton icon="close" label="Fermer" scale="sm" variant="tertiary" onClick={handleCancel} />
                    </div>

                    {/* ── Ligne 2 : Titre (NJHeading sm) + Toolbar ── */}
                    <div style={sty.titleRow}>
                        <NJHeading scale="sm">Editer les elements selectionnes</NJHeading>
                        <Toolbar
                            rowCount={rowCount}
                            canUndo={canUndo}
                            isFullscreen={isFullscreen}
                            onShortcuts={() => setShortcutsOpen(true)}
                            onDownload={handleDownload}
                            onUpload={handleUpload}
                            onCopyAll={handleCopyAll}
                            onUndo={handleUndo}
                            onToggleFullscreen={() => setIsFullscreen((v) => !v)}
                        />
                    </div>

                    {/* ── Onglets ── */}
                    <NJTabs label="Mass Editor" activeTab={activeTab}
                        onClickTabItem={(id) => setActiveTab(id as TabType)}>
                        {TABS.map((t) => (
                            <NJTab key={t} id={t} label={TAB_LABELS[t]} ariaControls={`panel-${t}`} />
                        ))}
                    </NJTabs>

                    {/* ── Grilles (une par onglet, cachees si inactives — comme app reelle) ── */}
                    <div style={sty.content}>
                        {TABS.map((t) => (
                            <div key={t} style={{ display: t === activeTab ? 'block' : 'none', height: '100%' }}>
                                <NJTabPanel id={`panel-${t}`} isActive={t === activeTab}>
                                    <MassEditorGridPanel
                                        testId={`me-${t.toLowerCase()}`}
                                        colDefs={COLDEFS_BY_TAB[t]}
                                        data={tableData[t] as unknown as object[]}
                                        onCellValueChanged={(e: AGCellValueChangedEvent) => onCellChanged(e, t)}
                                        onGridReady={handleGridReady}
                                        tabType={t}
                                        onToast={showToast}
                                        onUndoPush={(entry) => { undoStack.current.push(entry); setCanUndo(true); }}
                                    />
                                </NJTabPanel>
                            </div>
                        ))}
                    </div>

                    {/* ── Pied de page ── */}
                    <div style={sty.footer}>
                        <NJButton label="Annuler" variant="secondary" onClick={handleCancel} />
                        <NJButton
                            label={applyMutation.isPending ? 'Enregistrement...' : 'Valider'}
                            variant="primary"
                            disabled={applyMutation.isPending}
                            onClick={handleApply}
                        />
                    </div>
                </div>

                {shortcutsOpen && <ShortcutsModal onClose={() => setShortcutsOpen(false)} />}
                <NJToastContainer />
            </div>
        </PrototypeStudioLayout>
    );
}

const sty: Record<string, CSSProperties> = {
    // Page
    page: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 80px)',
        background: 'var(--nj-semantic-color-background-secondary-default)',
        padding: '24px 16px',
    },
    mapBg: { position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' },
    mapLbl: {
        position: 'absolute', bottom: 10, right: 14, display: 'flex', alignItems: 'center',
        fontSize: 11, color: 'var(--nj-semantic-color-text-secondary-default)',
        background: 'rgba(255,255,255,0.85)', padding: '3px 8px', borderRadius: 4,
    },

    // Modal responsive
    modal: {
        position: 'relative', zIndex: 10,
        display: 'flex', flexDirection: 'column',
        background: '#ffffff',
        borderRadius: 6, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        width: 'min(calc(100vw - 32px), 1600px)',
        height: 'min(calc(100vh - 80px), 800px)',
        overflow: 'hidden',
    },
    modalFs: {
        position: 'fixed', zIndex: 200, inset: 0,
        display: 'flex', flexDirection: 'column',
        background: '#ffffff', overflow: 'hidden',
    },

    // Ligne 1 : bouton Fermer seul (padding identique a NJModal)
    closeRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: '8px 12px 4px 20px',
        flexShrink: 0,
    },

    // Ligne 2 : titre NJHeading + toolbar (flex justify-between, mb-4 = 16px)
    titleRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px 16px 20px',
        flexShrink: 0,
    },

    // Toolbar
    toolbar: { display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 },
    lineChip: {
        display: 'flex', alignItems: 'center',
        background: 'var(--nj-semantic-color-background-secondary-default)',
        borderRadius: 4, paddingLeft: 8, height: 28, marginRight: 2,
    },
    lineText: { fontSize: 12, fontWeight: 600, color: 'var(--nj-semantic-color-text-secondary-default)', whiteSpace: 'nowrap' },
    divider: {
        display: 'inline-block', width: 1, height: 16,
        background: 'var(--nj-semantic-color-border-primary-default)',
        margin: '0 4px', flexShrink: 0,
    },

    // Zone tableau (flex 1, les grilles s'y etalent — 57vh depuis DataTable)
    content: { flex: 1, overflow: 'hidden', minHeight: 0, padding: '0 20px' },

    // Pied de page (identique a MassEditorActions.tsx)
    footer: {
        display: 'flex', justifyContent: 'flex-end', gap: 8,
        padding: '12px 16px',
        borderTop: '1px solid var(--nj-semantic-color-border-primary-default)',
        flexShrink: 0, background: '#ffffff',
    },

    // Overlay + modale raccourcis
    overlay: {
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    scPanel: {
        background: '#ffffff', borderRadius: 6, boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
        width: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
    },
    scHeader: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px', borderBottom: '1px solid var(--nj-semantic-color-border-primary-default)',
    },
    scList: { overflowY: 'auto', padding: '4px 20px 16px', display: 'flex', flexDirection: 'column' },
    scRow: {
        display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0',
        borderBottom: '1px solid var(--nj-semantic-color-border-primary-default)',
    },
    scKey: {
        display: 'inline-flex', alignItems: 'center',
        background: 'var(--nj-semantic-color-background-secondary-default)',
        border: '1px solid var(--nj-semantic-color-border-primary-default)',
        borderRadius: 4, padding: '2px 10px', fontSize: 12, fontFamily: 'monospace',
        whiteSpace: 'nowrap', minWidth: 180,
    },

    // Toast
    toast: {
        position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 16px', borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
        minWidth: 240, background: '#ffffff', border: '1px solid',
    },
    errState: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 16, height: 320,
    },
};
